import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';
import React, { ReactNode } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import ListItem from '../components/core/lists/ListItem';
import List from '../components/core/lists/List';
import Heading from '../components/core/typography/Heading';

/*
  Opt-in per singolo asset: se la Description dell'asset su Contentful è
  `align:right`, il logo va a capo su una riga propria, allineato a destra e
  più piccolo (es. il logo The Bloc su /gilead/, secondario rispetto a
  Gilead+Kite). Senza direttiva il comportamento resta invariato.

  `display` e `margin-left` vanno inline e non via classe: il CSS di
  gatsby-plugin-image è iniettato DOPO quello di Tailwind e
  `.gatsby-image-wrapper-constrained{display:inline-block}` ha la stessa
  specificità di `.block`, quindi vincerebbe per ordine. Inline batte entrambi.
  `display:block` manda l'immagine a capo, `margin-left:auto` la spinge a
  destra (su un box inline-block i margini auto varrebbero 0).

  La larghezza resta una classe perché serve responsive, impossibile inline.
  Va scritta per intero: il purge AOT di Tailwind 2 estrae token di testo,
  quindi un nome costruito (`w-${...}`) verrebbe rimosso in silenzio.
*/
const embeddedAsset = (node: Block | Inline) => {
  // L'asset è risolto solo se la query del template chiede `references`
  // accanto a `raw`; senza, arriva un link non risolto e non c'è nulla da
  // rendere.
  const image = getImage(node.data.target);
  if (!image) return null;

  const alignRight = node.data.target.description === 'align:right';

  return (
    <GatsbyImage
      className={alignRight ? 'my-10 w-32 md:w-40' : 'my-10'}
      image={image}
      alt={node.data.target.title || ''}
      style={
        alignRight
          ? { maxHeight: '400px', display: 'block', marginLeft: 'auto' }
          : { maxHeight: '400px' }
      }
      imgStyle={{ objectFit: 'contain' }}
    />
  );
};

export const renderOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h1" className="mt-10">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h2" className="mt-10">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h3" className="mt-10">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_4]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h4" className="mt-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_5]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h5" className="mt-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_6]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h6" className="mt-3">
          {children}
        </Heading>
      );
    },
    [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
      return <p className="text-paragraph mb-4">{children}</p>;
    },
    [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => {
      return <List>{children}</List>;
    },
    [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: ReactNode) => {
      return <ListItem className="-mt-4 first:mt-0">{children}</ListItem>;
    },
    [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
      return (
        <a className="text-primary-500 font-bold" href={node.data.uri}>
          {children}
        </a>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: embeddedAsset,
  },
  renderText: (text: string) =>
    text.split('\\n').flatMap((text, i) => [i > 0 && <br />, text]),
};

export const renderOptionsLowMarginHeaders = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h1" className="mb-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h2" className="mb-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h3" className="mb-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_4]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h4" className="mb-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_5]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h5" className="mb-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.HEADING_6]: (node: Block | Inline, children: ReactNode) => {
      return (
        <Heading variant="h6" className="mb-4">
          {children}
        </Heading>
      );
    },
    [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
      return <p className="text-paragraph mb-4">{children}</p>;
    },
    [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => {
      return <List>{children}</List>;
    },
    [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: ReactNode) => {
      return <ListItem className="-mt-4 first:mt-0">{children}</ListItem>;
    },
    [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
      return (
        <a className="text-primary-500 font-bold" href={node.data.uri}>
          {children}
        </a>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: embeddedAsset,
  },
  renderText: (text: string) =>
    text.split('\\n').flatMap((text, i) => [i > 0 && <br />, text]),
};
